import React from "react";
import { Check, X, Minus } from "lucide-react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";
import { useIsAndroidApp } from "@/hooks/useLaunchContext";

// Aligné sur les offres actuelles : prix unique 9,99 € / $9.99 par chanson + abonnements
// mensuels optionnels (Pack Plus / Premium). Le prix chiffré n'est jamais affiché dans
// l'app Android (Google Play Billing gère les prix localisés).
const ROWS = [
  { fr: "Prix unique : 9,99 € la chanson complète", en: "Flat $9.99 per complete song",
    frApp: "Prix unique et transparent par chanson", enApp: "One transparent flat price per song",
    kalypso: true, others: false },
  { fr: "Paroles 100% personnalisées par IA", en: "100% personalized AI-written lyrics", kalypso: true, others: "partial" },
  { fr: "Voix chantée réaliste, mixée et masterisée", en: "Realistic sung vocals, mixed & mastered", kalypso: true, others: false },
  { fr: "MP3 téléchargeable, à vous pour la vie", en: "Downloadable MP3, yours for life", kalypso: true, others: "partial" },
  { fr: "Livrée en quelques minutes", en: "Delivered in minutes", kalypso: true, others: false },
  { fr: "Pochette d'album générée par IA", en: "AI-generated album cover", kalypso: true, others: false },
  { fr: "Votre propre voix intégrée dans la chanson", en: "Your own voice embedded in the song", kalypso: true, others: false },
  { fr: "Publication de votre musique sur YouTube", en: "Your song published on YouTube", kalypso: true, others: false },
  { fr: "Certificat cadeau imprimable avec QR code", en: "Printable gift certificate with QR code", kalypso: true, others: false },
  { fr: "Abonnements mensuels économiques (Starter / Premium / Pro / Royal)", en: "Money-saving monthly plans (Starter / Premium / Pro / Royal)", kalypso: true, others: "partial" },
  { fr: "Régénération gratuite en cas d'échec technique", en: "Free regeneration on technical failure", kalypso: true, others: false },
];

function Cell({ value }) {
  if (value === true) return <Check className="mx-auto h-5 w-5 text-emerald-400" />;
  if (value === "partial") return <Minus className="mx-auto h-5 w-5 text-amber-400" />;
  return <X className="mx-auto h-5 w-5 text-muted-foreground/40" />;
}

export default function ComparisonTable() {
  const { t, language } = useI18n();
  const isAndroidApp = useIsAndroidApp();
  const label = (row) => {
    if (isAndroidApp && row.frApp) return row[language === "en" ? "enApp" : "frApp"];
    return row[language] || row.fr;
  };

  return (
    <section className="py-24 sm:py-32" data-testid="landing-comparison-table">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-medium tracking-tight sm:text-3xl lg:text-4xl">
            {t("landing.comparisonTitle")}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm text-muted-foreground sm:text-base">
            {t("landing.comparisonSubtitle")}
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-12 overflow-hidden rounded-2xl border border-border bg-card"
        >
          <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2 border-b border-border bg-primary/5 px-4 py-4 sm:gap-4 sm:px-6">
            <span className="text-xs font-medium text-muted-foreground sm:text-sm">&nbsp;</span>
            <span className="w-24 text-center font-heading text-sm font-semibold text-primary sm:w-32 sm:text-base">Kalypso Music</span>
            <span className="w-24 text-center text-xs font-medium text-muted-foreground sm:w-32 sm:text-sm">
              {t("landing.comparisonOthers")}
            </span>
          </div>
          {ROWS.map((row, i) => (
            <div
              key={i}
              data-testid={`landing-comparison-row-${i + 1}`}
              className={`grid grid-cols-[1fr_auto_auto] items-center gap-2 px-4 py-4 text-sm sm:gap-4 sm:px-6 ${
                i % 2 === 0 ? "bg-transparent" : "bg-muted/20"
              }`}
            >
              <span className="text-foreground/90">{label(row)}</span>
              <span className="w-24 sm:w-32"><Cell value={row.kalypso} /></span>
              <span className="w-24 sm:w-32"><Cell value={row.others} /></span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
